import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ThumbsUp, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import type { CommunityPost } from '@/types';

const MOCK_POSTS: CommunityPost[] = [
  {
    id: '1',
    title: 'How are you handling return-to-office policies in 2026?',
    content: 'We are debating between a hybrid model and full flexibility. Would love to hear what other CPOs are doing and what retention impact you have seen.',
    author: { id: 'u1', email: 'sarah@techcorp.com', orgId: 'o1', role: 'admin', name: 'Sarah Chen' },
    topic: 'Remote Work',
    createdAt: '2026-03-20T14:30:00Z',
    replyCount: 23,
    likeCount: 47,
  },
  {
    id: '2',
    title: 'Compensation benchmarking for Series C startups',
    content: 'Looking for data on how Series C companies are structuring comp packages, especially equity refreshers. Our attrition in engineering is climbing and I suspect comp is a factor.',
    author: { id: 'u2', email: 'mark@startupx.com', orgId: 'o2', role: 'admin', name: 'Mark Thompson' },
    topic: 'Compensation',
    createdAt: '2026-03-18T09:15:00Z',
    replyCount: 15,
    likeCount: 32,
  },
  {
    id: '3',
    title: 'Best practices for measuring DEI progress',
    content: 'We set ambitious DEI goals last year but our metrics are not moving. What frameworks are working for others? Especially interested in how you measure inclusion beyond demographic data.',
    author: { id: 'u3', email: 'priya@globalinc.com', orgId: 'o3', role: 'admin', name: 'Priya Patel' },
    topic: 'DEI',
    createdAt: '2026-03-15T16:45:00Z',
    replyCount: 31,
    likeCount: 58,
  },
  {
    id: '4',
    title: 'Manager quality as a retention lever',
    content: 'Our exit interview data consistently shows that manager quality is the top reason people leave. We are building a manager effectiveness program. Has anyone done something similar?',
    author: { id: 'u4', email: 'alex@megacorp.com', orgId: 'o4', role: 'admin', name: 'Alex Kim' },
    topic: 'Retention',
    createdAt: '2026-03-12T11:00:00Z',
    replyCount: 42,
    likeCount: 89,
  },
  {
    id: '5',
    title: 'Using AI in workforce planning',
    content: 'We are experimenting with predictive models for attrition risk scoring. Early results are promising but the team is nervous about bias in the model. How are others approaching this?',
    author: { id: 'u5', email: 'jordan@datadriven.com', orgId: 'o5', role: 'admin', name: 'Jordan Lee' },
    topic: 'Retention',
    createdAt: '2026-03-10T08:30:00Z',
    replyCount: 19,
    likeCount: 41,
  },
  {
    id: '6',
    title: 'Pay transparency laws impact on hiring',
    content: 'With new pay transparency legislation in several states, we are adjusting our hiring process and comp communication. What changes have you made?',
    author: { id: 'u6', email: 'nat@hrforward.com', orgId: 'o6', role: 'admin', name: 'Natalie Fischer' },
    topic: 'Compensation',
    createdAt: '2026-03-08T13:20:00Z',
    replyCount: 27,
    likeCount: 53,
  },
];

const TOPICS = ['All', 'Retention', 'Compensation', 'DEI', 'Remote Work'];

export default function CommunityPage() {
  const [activeTopic, setActiveTopic] = useState('All');
  const [showNewPost, setShowNewPost] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newTopic, setNewTopic] = useState('Retention');
  const [posts, setPosts] = useState(MOCK_POSTS);

  const filtered = activeTopic === 'All'
    ? posts
    : posts.filter((p) => p.topic === activeTopic);

  const handleCreatePost = () => {
    if (!newTitle || !newContent) return;
    const post: CommunityPost = {
      id: String(Date.now()),
      title: newTitle,
      content: newContent,
      author: { id: 'me', email: 'me@company.com', orgId: 'org1', role: 'admin', name: 'You' },
      topic: newTopic,
      createdAt: new Date().toISOString(),
      replyCount: 0,
      likeCount: 0,
    };
    setPosts([post, ...posts]);
    setShowNewPost(false);
    setNewTitle('');
    setNewContent('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Community</h1>
          <p className="mt-1 text-sm text-gray-500">
            Connect with CPOs and HR leaders across industries
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowNewPost(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Post
        </button>
      </div>

      {/* New Post Form */}
      {showNewPost && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Create a Post</h3>
            <button onClick={() => setShowNewPost(false)} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="input-field"
              placeholder="Post title"
            />
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              className="input-field min-h-[100px]"
              placeholder="Share your question or insight..."
            />
            <div className="flex items-center justify-between">
              <select
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                className="input-field w-48"
              >
                {TOPICS.filter((t) => t !== 'All').map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button className="btn-primary" onClick={handleCreatePost}>
                Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {TOPICS.map((topic) => (
          <button
            key={topic}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTopic === topic
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTopic(topic)}
          >
            {topic}
          </button>
        ))}
      </div>

      {/* Post List */}
      <div className="space-y-4">
        {filtered.map((post) => (
          <Link
            key={post.id}
            to={`/community/${post.id}`}
            className="card block hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                    {post.topic}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {post.title}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {post.content}
                </p>
                <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                  <span>{post.author.name}</span>
                  <span>{format(new Date(post.createdAt), 'MMM d, yyyy')}</span>
                </div>
              </div>
              <div className="ml-6 flex flex-col items-end gap-2 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />
                  {post.replyCount}
                </div>
                <div className="flex items-center gap-1">
                  <ThumbsUp className="h-4 w-4" />
                  {post.likeCount}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
